import useAccount from '@rhg/hooks/use-account';
import { useActiveCircleId } from '@rhg/hooks/use-active-circle';
import useCircles from '@rhg/hooks/use-circles';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { AutoSelectCircleState } from './types';

export const AutoSelectCircleContext = createContext<AutoSelectCircleState>(
    undefined!
);

/**
 * There are three ways a circle can be selected
 * 1) If the user only has one circle it is selected by default
 * 2) If there is a recipientId in the query parameter
 * 3) If the user is an ACR in a circle
 */
export function AutoSelectCircleProvider({
    children,
}: {
    children: ReactNode | ReactNode[];
}) {
    const [isCircleSelectionLoaded, setIsCircleSelectionLoaded] =
        useState(false);
    const { isAuthenticated } = useAuth();
    const { circles, isFetched: areCirclesFetched } = useCircles();
    const { hasActiveCircle, setActiveCircleId, activeCircleId } =
        useActiveCircleId(); // Use this for speed vs. useActiveCircle
    const { account, isFetched: isAccountFetched } = useAccount();

    const circleCount = useMemo(() => circles?.length ?? 0, [circles?.length]);

    const selectCircle = useCallback(
        (circleId: number) => {
            setActiveCircleId(circleId);
            setIsCircleSelectionLoaded(true);
        },
        [setActiveCircleId]
    );

    const arePrerequisitesFetched = useMemo(
        () => isAuthenticated && areCirclesFetched && isAccountFetched,
        [areCirclesFetched, isAccountFetched, isAuthenticated]
    );

    useEffect(() => {
        if (!arePrerequisitesFetched) {
            return;
        }

        /**
         * If we already have an active circle, the selection was loaded
         */
        if (
            hasActiveCircle &&
            circles?.some((circle) => circle.recipient.id === activeCircleId)
        ) {
            setIsCircleSelectionLoaded(true);
            return;
        }

        /**
         * set active circle if the user only has one circle
         */
        if (
            (!hasActiveCircle ||
                !circles?.some(
                    (circle) => circle.recipient.id === activeCircleId
                )) &&
            circleCount === 1
        ) {
            /**
             * If there is only one circle for the user then it should be selected
             * automatically.
             */
            selectCircle(circles![0]!.recipient!.id);
            return;
        }

        /**
         * If there are multiple circles and none are selected select the one in which the user is the recipient
         */
        const activeRecipientCircle = account?.userId
            ? circles?.find(
                  (circle) => circle.recipient.userId === account?.userId
              )
            : null;

        if (circleCount > 1 && activeRecipientCircle) {
            selectCircle(activeRecipientCircle.recipient.id);
            return;
        }

        // Loaded but nothing was selected
        setIsCircleSelectionLoaded(true);

        // TODO multiple circles with no active circle?
    }, [
        account?.userId,
        areCirclesFetched,
        arePrerequisitesFetched,
        circleCount,
        circles,
        hasActiveCircle,
        isAuthenticated,
        selectCircle,
        activeCircleId,
        setActiveCircleId,
    ]);

    return (
        <AutoSelectCircleContext.Provider
            value={{
                isCircleSelectionLoaded,
            }}
        >
            {children}
        </AutoSelectCircleContext.Provider>
    );
}
